import { logError } from './log';
import type { AreaSafe, Image, ImageSize } from 'types';

export const CLOUDINARY_CLOUD_NAME = window.app.env.CLOUDINARY_CLOUD_NAME || 'collabary-test';
export const CLOUDINARY_PRESET = window.app.env.CLOUDINARY_PRESET || 'kemxzxuj'; // https://cloudinary.com/console/settings/upload
export const CLOUDINARY_VIDEO_UPLOAD = `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/video/upload`;
const CLOUDINARY_VIDEO_RES_URL = `https://res.cloudinary.com/${CLOUDINARY_CLOUD_NAME}/video/upload/`;

// to show better images for retina for example
// cloudinary will have to cache 2 sizes of the same image (1x size and 2x size)
const PIXEL_RATIO = (window.devicePixelRatio && window.devicePixelRatio > 1) ? 2 : 1;
const CLOUDINARY_API = `https://res.cloudinary.com/${CLOUDINARY_CLOUD_NAME}/image/fetch`;
const AUTO_CROPPING_SETTINGS = 'c_fill,g_face,f_auto';
const AUTO_CROPPING_RESPECT_RATIO_SETTINGS = 'c_fit,f_auto';
const ROUNDED_IMAGE_SETTINGS = 'r_max,f_png';

export function hasArea(image: Image): boolean {
  if (!image || !image.area) {
    return false;
  }

  const area = image.area;
  const requiredFields = ['x', 'y', 'width', 'height', 'scale'];
  const additionalFields = ['originalWidth', 'originalHeight', 'rotate'];

  const hasAllRequiredFields = requiredFields.every(field => (field in area));

  // @ts-ignore - TS assumes that area.scale could have been changed in the last line to null/undefined, hence throws an error.
  if (hasAllRequiredFields && area.scale < 1) {
    const hasAdditionalFields = additionalFields.every(field => (field in area));
    return hasAllRequiredFields && hasAdditionalFields;
  }

  return hasAllRequiredFields;
}

export function relativeValueToString(value: number): string {
  if (value <= 0) {
    return '0.0';
  }

  if (Math.floor(value) === value) {
    return `${value}.0`;
  }

  return String(value);
}

function pipeWithCroppingArea(image: Image, isSquareImage?: boolean): string {
  let proxyUrl = '';

  if (hasArea(image)) {
    const area: AreaSafe = (image.area as any);
    const includeAspectRatio = !(area.scale < 1 && area.originalWidth > 0 && area.originalHeight > 0) && isSquareImage;
    const dimensions = {
      x: Math.max(area.x, 0),
      y: Math.max(area.y, 0),
      width: area.width + Math.min(area.x, 0),
      height: area.height + Math.min(area.y, 0)
    };

    proxyUrl += `/x_${relativeValueToString(dimensions.x)}`;
    proxyUrl += `,y_${relativeValueToString(dimensions.y)}`;
    proxyUrl += `,w_${relativeValueToString(dimensions.width)}`;
    proxyUrl += includeAspectRatio ? ',ar_1,c_lpad' : `,h_${relativeValueToString(dimensions.height)}`;
    proxyUrl += ',c_crop';
  }

  return proxyUrl;
}

function pipeWithTopAndLeftPaddings(area: AreaSafe): string {
  if (area.x >= 0 && area.y >= 0) {
    return '';
  }

  let width = area.originalWidth * (1 - area.x);
  let height = area.originalHeight * (1 - area.y);

  width = Math.min(width, area.originalWidth * area.width);
  height = Math.min(height, area.originalHeight * area.height);

  // add a white space (top, left)
  return `/w_${Math.round(width)},h_${Math.round(height)},c_lpad,b_white,g_south_east`;
}

function pipeWithRightAndBottomPaddings(area: AreaSafe): string {
  const width = area.originalWidth * area.width;
  const height = area.originalHeight * area.height;

  // add a white space (bottom, right)
  return `/w_${Math.round(width)},h_${Math.round(height)},c_lpad,b_white,g_north_west`;
}

function pipeWithZoomOut(image: Image): string {
  let proxyUrl = '';

  if (hasArea(image)) {
    const area: AreaSafe = (image.area as any);

    if (area.scale < 1 && area.originalWidth > 0 && area.originalHeight > 0) {
      proxyUrl += pipeWithTopAndLeftPaddings(area);
      proxyUrl += pipeWithRightAndBottomPaddings(area);
    }
  }

  return proxyUrl;
}

function pipeWithSize(image: Image, size?: ImageSize | null) {
  let proxyUrl = '';

  if (!size) {
    return proxyUrl;
  }

  if (hasArea(image)) {
    const area: AreaSafe = (image.area as any);

    proxyUrl += `/w_${size.width},h_${size.height},c_fill`; // get required size
    if (area.rotate) {
      proxyUrl += `,a_${area.rotate}`;
    }
  } else {
    const autoCroppingSettings = size.preserveAspectRatio ? AUTO_CROPPING_RESPECT_RATIO_SETTINGS : AUTO_CROPPING_SETTINGS;
    const roundedImageSettings = size.roundedImage ? ROUNDED_IMAGE_SETTINGS : '';

    const urlOptions = [
      `/w_${size.width}`,
      `h_${size.height}`,
      `${autoCroppingSettings}`,
      `${roundedImageSettings}`
    ].filter(v => v !== '').join(',');

    proxyUrl += urlOptions; // get required size + auto crop + rounded image settings
  }

  proxyUrl += `/dpr_${PIXEL_RATIO.toFixed(1)}`; // pass device pixel ratio

  return proxyUrl;
}

// if size was not specified, it will apply only cropping `area`
export function getCloudinaryImageUrl(
  image?: Image,
  size: ImageSize | undefined | null = null,
  isSquareImage?: boolean): string {
  if (!image || !image.url) {
    return '';
  }

  let proxyUrl = CLOUDINARY_API;

  proxyUrl += pipeWithCroppingArea(image, isSquareImage);
  proxyUrl += pipeWithZoomOut(image);
  proxyUrl += pipeWithSize(image, size);

  proxyUrl += `/${encodeURIComponent(image.url)}`; // pass image url

  return proxyUrl;
}

export function getDefaultCenteredArea() {
  return ({
    x: 0.5,
    y: 0.5,
    scale: 1,
    width: 0,
    height: 0
  });
}

export function getCloudinaryVideoThumbnail(url: string = ''): string {
  if (url.length === 0) {
    return '';
  }

  const parts = url.split('/');
  const videoName = parts[parts.length - 1];
  const videoExtension = videoName.split('.')[1];

  return url.replace('http://', 'https://').replace(videoExtension, 'jpg');
}

export function getCloudinaryVideoPublicId(url: string = ''): string {
  if (!url) {
    return '';
  }

  let publicId = '';

  try {
    const parts = url.split('/');
    const videoName = parts[parts.length - 1];
    publicId = videoName.split('.')[0];
  } catch (e) {
    logError(e);
  }

  return publicId;
}

export const isCloudinaryVideoResourceUrl = (url: string): boolean => (new RegExp(CLOUDINARY_VIDEO_RES_URL)).test(url);

export const addQualityParamToCloudinaryUrl = (url: string): string => {
  if (!isCloudinaryVideoResourceUrl(url)) {
    return url;
  }

  const restOfUrl = url.replace(CLOUDINARY_VIDEO_RES_URL, '');

  if (restOfUrl.split('/').length !== 2) {
    return url;
  }

  return `${CLOUDINARY_VIDEO_RES_URL}q_auto/${restOfUrl}`;
};
